// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AdvisoryDebtAccount } from '@shared/data-access/types/brokerage-types';
import { produce } from 'immer';
import { resolveInstitutionLabel } from './debt-amortizations';

export type DebtBreakdownSlice = {
  labels: string[];
  weights: number[];
};

const toBreakdownSlice = (
  items: { label: string; value: number }[],
): DebtBreakdownSlice => {
  const totalDebt = items.reduce((sum, item) => sum + item.value, 0);

  if (totalDebt <= 0) {
    return { labels: [], weights: [] };
  }

  return {
    labels: items.map(item => item.label),
    weights: items.map(item => item.value / totalDebt),
  };
};

export const buildDebtBreakdownByBank = (
  accounts: AdvisoryDebtAccount[] = [],
  institutionNames: Map<string, string> = new Map(),
): DebtBreakdownSlice => {
  const totalsByInstitution = accounts
    .flatMap(account => {
      const institutionId = account.institution_id;
      const institutionLabel = resolveInstitutionLabel(
        institutionId,
        institutionNames,
      );

      return [
        ...(account.loans ?? []).map(loan => ({
          key: institutionId,
          label: institutionLabel,
          amount: loan.pending_amount ?? 0,
        })),
        ...(account.credit_lines ?? []).map(creditLine => ({
          key: institutionId,
          label: institutionLabel,
          amount: creditLine.disposed ?? 0,
        })),
      ].filter(product => product.amount > 0);
    })
    .reduce(
      (acc, product) =>
        produce(acc, draft => {
          if (draft.has(product.key)) {
            draft.get(product.key)!.value += product.amount;
          } else {
            draft.set(product.key, {
              label: product.label,
              value: product.amount,
            });
          }
        }),
      new Map<string, { label: string; value: number }>(),
    );

  return toBreakdownSlice(Array.from(totalsByInstitution.values()));
};

export const buildDebtBreakdownByType = (
  accounts: AdvisoryDebtAccount[] = [],
): DebtBreakdownSlice => {
  const totalsByType = accounts
    .flatMap(account => [
      ...(account.loans ?? []).map(loan => ({
        type: loan.name ?? 'loan',
        amount: loan.pending_amount ?? 0,
      })),
      ...(account.credit_lines ?? []).map(creditLine => ({
        type: creditLine.product_type ?? 'unknown',
        amount: creditLine.disposed ?? 0,
      })),
    ])
    .filter(product => product.amount > 0)
    .reduce(
      (acc, product) =>
        produce(acc, draft => {
          draft.set(product.type, (draft.get(product.type) ?? 0) + product.amount);
        }),
      new Map<string, number>(),
    );

  return toBreakdownSlice(
    Array.from(totalsByType.entries(), ([type, value]) => ({
      label: type,
      value,
    })),
  );
};
